import { useEffect, useMemo, useState } from "react"
import { CheckCircle2, Eye, EyeOff, Loader2, RefreshCw, Save, Search, XCircle } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  getAdminSongs,
  hideAdminSong,
  unhideAdminSong,
  updateAdminSong,
  verifyAdminSong,
} from "@/lib/api"

const sortOptions = [
  { label: "Updated (Newest)", sortBy: "updatedAt", sortOrder: "desc" },
  { label: "Updated (Oldest)", sortBy: "updatedAt", sortOrder: "asc" },
  { label: "Title (A-Z)", sortBy: "title", sortOrder: "asc" },
  { label: "Title (Z-A)", sortBy: "title", sortOrder: "desc" },
  { label: "Most Played", sortBy: "playCount", sortOrder: "desc" },
  { label: "Most Queued", sortBy: "queueCount", sortOrder: "desc" },
  { label: "Most Searched", sortBy: "searchCount", sortOrder: "desc" },
]

function emptyEditState(song) {
  return {
    title: song?.title || "",
    channel: song?.channel || "",
    thumbnail: song?.thumbnail || "",
    duration: String(song?.duration ?? 0),
    hidden: Boolean(song?.hidden),
    verified: Boolean(song?.verified),
  }
}

function formatDate(value) {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString()
}

function formatSeconds(value) {
  const seconds = Number(value || 0)
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00"
  const minutes = Math.floor(seconds / 60)
  const remaining = Math.floor(seconds % 60)
  return `${minutes}:${String(remaining).padStart(2, "0")}`
}

export default function SongCatalogPage() {
  const [query, setQuery] = useState("")
  const [draftQuery, setDraftQuery] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [sortBy, setSortBy] = useState("updatedAt")
  const [sortOrder, setSortOrder] = useState("desc")
  const [includeHidden, setIncludeHidden] = useState(true)

  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const [selectedSong, setSelectedSong] = useState(null)
  const [editState, setEditState] = useState(emptyEditState(null))
  const [saving, setSaving] = useState(false)

  const [busyByVideo, setBusyByVideo] = useState({})

  const hasSongs = Boolean(result?.items?.length)

  const selectedSort = useMemo(
    () => sortOptions.find((option) => option.sortBy === sortBy && option.sortOrder === sortOrder),
    [sortBy, sortOrder]
  )

  const loadSongs = async (opts = {}) => {
    setLoading(true)
    setError("")
    try {
      const data = await getAdminSongs({
        q: opts.query ?? query,
        page: opts.page ?? page,
        page_size: opts.pageSize ?? pageSize,
        sort_by: opts.sortBy ?? sortBy,
        sort_order: opts.sortOrder ?? sortOrder,
        include_hidden: opts.includeHidden ?? includeHidden,
      })
      setResult(data)

      if (selectedSong) {
        const nextSelected = data.items?.find((item) => item.video_id === selectedSong.video_id)
        if (nextSelected) {
          setSelectedSong(nextSelected)
          setEditState(emptyEditState(nextSelected))
        }
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load song catalog")
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadSongs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortOrder, includeHidden, query])

  const handleSearchSubmit = (event) => {
    event.preventDefault()
    setPage(1)
    setQuery(draftQuery.trim())
  }

  const openDetails = (song) => {
    setSelectedSong(song)
    setEditState(emptyEditState(song))
  }

  const closeDetails = () => {
    setSelectedSong(null)
    setEditState(emptyEditState(null))
  }

  const runRowAction = async (videoId, action) => {
    setBusyByVideo((prev) => ({ ...prev, [videoId]: true }))
    try {
      await action()
      await loadSongs()
    } finally {
      setBusyByVideo((prev) => ({ ...prev, [videoId]: false }))
    }
  }

  const handleSave = async () => {
    if (!selectedSong) return
    setSaving(true)
    try {
      const payload = {
        title: editState.title.trim(),
        channel: editState.channel.trim(),
        thumbnail: editState.thumbnail.trim(),
        duration: Math.max(Number(editState.duration) || 0, 0),
        hidden: Boolean(editState.hidden),
        verified: Boolean(editState.verified),
      }
      const updated = await updateAdminSong(selectedSong.video_id, payload)
      setSelectedSong(updated)
      setEditState(emptyEditState(updated))
      await loadSongs()
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save song metadata")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Card className="glass border-white/5 overflow-hidden">
        <CardContent className="p-6 sm:p-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-purple-300 mb-2">Developer Console</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-[Outfit] leading-tight">Song Catalog</h2>
            <p className="text-zinc-400 mt-2 max-w-2xl">
              Manage the existing song catalog collection: search songs, inspect metadata and analytics, edit records, and toggle hide/verify state.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
            onClick={() => loadSongs()}
            disabled={loading}
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
            Refresh
          </Button>
        </CardContent>
      </Card>

      <Card className="glass border-white/5">
        <CardHeader className="p-6 pb-3">
          <CardTitle className="text-sm uppercase tracking-[0.25em] text-zinc-500">Search, Sort, Pagination</CardTitle>
        </CardHeader>
        <CardContent className="p-6 pt-0 space-y-4">
          <form onSubmit={handleSearchSubmit} className="flex flex-col gap-3 lg:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
              <Input
                value={draftQuery}
                onChange={(event) => setDraftQuery(event.target.value)}
                className="pl-9"
                placeholder="Search by title, channel, or video id"
              />
            </div>
            <Button type="submit">Search</Button>
          </form>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="space-y-2">
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Sort</span>
              <select
                className="input-base"
                value={`${sortBy}:${sortOrder}`}
                onChange={(event) => {
                  const [nextSortBy, nextSortOrder] = event.target.value.split(":")
                  setSortBy(nextSortBy)
                  setSortOrder(nextSortOrder)
                  setPage(1)
                }}
              >
                {sortOptions.map((option) => (
                  <option key={`${option.sortBy}:${option.sortOrder}`} value={`${option.sortBy}:${option.sortOrder}`}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-2">
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Page Size</span>
              <select
                className="input-base"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number(event.target.value))
                  setPage(1)
                }}
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-2">
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Include Hidden</span>
              <select
                className="input-base"
                value={includeHidden ? "yes" : "no"}
                onChange={(event) => {
                  setIncludeHidden(event.target.value === "yes")
                  setPage(1)
                }}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>

            <div className="space-y-2">
              <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">Result Info</span>
              <div className="rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-zinc-300">
                {result ? `${result.total.toLocaleString()} total, page ${result.page} / ${result.totalPages}` : "-"}
              </div>
            </div>
          </div>

          {selectedSort ? (
            <p className="text-xs text-zinc-500 uppercase tracking-[0.15em]">Current sort: {selectedSort.label}</p>
          ) : null}
        </CardContent>
      </Card>

      {error ? (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card className="glass border-white/5">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-sm uppercase tracking-[0.25em] text-zinc-500">Songs</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {loading ? (
              <div className="h-44 flex items-center justify-center text-zinc-400 gap-2">
                <Loader2 className="animate-spin" size={16} />
                Loading songs...
              </div>
            ) : !hasSongs ? (
              <div className="h-44 flex items-center justify-center text-zinc-500">No songs found</div>
            ) : (
              <div className="space-y-3">
                {result.items.map((song) => {
                  const busy = Boolean(busyByVideo[song.video_id])
                  return (
                    <div
                      key={song.video_id}
                      className="rounded-2xl border border-white/10 bg-zinc-900/40 p-4 hover:border-purple-500/30 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <img
                          src={song.thumbnail || "https://placehold.co/160x90/18181b/a1a1aa?text=No+Image"}
                          alt={song.title || "Song"}
                          className="w-28 h-16 rounded-lg object-cover border border-white/10 shrink-0"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-white truncate">{song.title || "Untitled"}</div>
                          <div className="text-sm text-zinc-400 truncate">{song.channel || "Unknown channel"}</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge variant="outline" className="border-white/10 bg-white/5 text-zinc-200">
                              {song.video_id}
                            </Badge>
                            <Badge variant="outline" className="border-white/10 bg-white/5 text-zinc-200">
                              {formatSeconds(song.duration)}
                            </Badge>
                            {song.hidden ? (
                              <Badge variant="outline" className="border-amber-500/30 bg-amber-500/10 text-amber-200">Hidden</Badge>
                            ) : (
                              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-200">Visible</Badge>
                            )}
                            {song.verified ? (
                              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-200">Verified</Badge>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button type="button" size="sm" variant="outline" className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10" onClick={() => openDetails(song)}>
                          Details / Edit
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
                          onClick={() =>
                            runRowAction(song.video_id, () =>
                              song.hidden ? unhideAdminSong(song.video_id) : hideAdminSong(song.video_id)
                            )
                          }
                          disabled={busy}
                        >
                          {busy ? <Loader2 className="animate-spin" size={14} /> : song.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                          {song.hidden ? "Unhide" : "Hide"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
                          onClick={() => runRowAction(song.video_id, () => verifyAdminSong(song.video_id))}
                          disabled={busy || song.verified}
                        >
                          {busy ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                          {song.verified ? "Verified" : "Verify"}
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            <div className="mt-5 flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="outline"
                className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
                disabled={loading || !result || result.page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <div className="text-sm text-zinc-400">
                {result ? `Page ${result.page} of ${result.totalPages}` : "Page -"}
              </div>
              <Button
                type="button"
                variant="outline"
                className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
                disabled={loading || !result || result.page >= result.totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                Next
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="glass border-white/5">
          <CardHeader className="p-6 pb-3">
            <CardTitle className="text-sm uppercase tracking-[0.25em] text-zinc-500">Details / Edit</CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-4">
            {!selectedSong ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-zinc-400 text-sm">
                Select a song from the list to inspect metadata and analytics, then apply edits.
              </div>
            ) : (
              <>
                <div className="space-y-2 text-sm">
                  <div className="text-zinc-400">Video ID</div>
                  <div className="text-zinc-200 break-all">{selectedSong.video_id}</div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Plays</div>
                    <div className="text-xl font-semibold text-white mt-1">{selectedSong.play_count.toLocaleString()}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Queues</div>
                    <div className="text-xl font-semibold text-white mt-1">{selectedSong.queue_count.toLocaleString()}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Searches</div>
                    <div className="text-xl font-semibold text-white mt-1">{selectedSong.search_count.toLocaleString()}</div>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Cache Hits</div>
                    <div className="text-xl font-semibold text-white mt-1">{selectedSong.cache_hits.toLocaleString()}</div>
                  </div>
                </div>

                <div className="grid gap-2 text-xs text-zinc-400">
                  <div>Created: {formatDate(selectedSong.created_at)}</div>
                  <div>Updated: {formatDate(selectedSong.updated_at)}</div>
                  <div>Last queued: {formatDate(selectedSong.last_queued_at)}</div>
                  <div>Last played: {formatDate(selectedSong.last_played_at)}</div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-[0.18em]">Title</Label>
                    <Input
                      value={editState.title}
                      onChange={(event) => setEditState((prev) => ({ ...prev, title: event.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-[0.18em]">Channel</Label>
                    <Input
                      value={editState.channel}
                      onChange={(event) => setEditState((prev) => ({ ...prev, channel: event.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-[0.18em]">Thumbnail URL</Label>
                    <Input
                      value={editState.thumbnail}
                      onChange={(event) => setEditState((prev) => ({ ...prev, thumbnail: event.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs uppercase tracking-[0.18em]">Duration (seconds)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editState.duration}
                      onChange={(event) => setEditState((prev) => ({ ...prev, duration: event.target.value }))}
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
                    onClick={() => setEditState((prev) => ({ ...prev, hidden: !prev.hidden }))}
                  >
                    {editState.hidden ? <Eye size={14} /> : <EyeOff size={14} />}
                    {editState.hidden ? "Marked Hidden" : "Marked Visible"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
                    onClick={() => setEditState((prev) => ({ ...prev, verified: !prev.verified }))}
                  >
                    {editState.verified ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                    {editState.verified ? "Marked Verified" : "Not Verified"}
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={handleSave} disabled={saving}>
                    {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                    Save Metadata
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
                    onClick={() => setEditState(emptyEditState(selectedSong))}
                    disabled={saving}
                  >
                    Reset
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/10 bg-white/5 text-zinc-200 hover:bg-white/10"
                    onClick={closeDetails}
                    disabled={saving}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
